/**
 * A playbook as one plain sentence, from the catalog's own words:
 * "When a customer writes · if the message is small talk is greeting → reply to the customer in your
 * brand voice, then stop". Used in the list, the template gallery and the Test trace.
 */

import type {
	ActionStep,
	IfStep,
	OutcomeStep,
	RuleCondition,
	RuleDefinition,
	RuleStep,
	RuleTrigger,
	RulesCatalog
} from '@/types/rules.types';
import { outcomesFor } from './rule-tree';
import { valueInputFor } from './value-input';

// Names keep their capital mid-sentence: "Kim checks", "Advanced AI".
const KEEPS_CAPITAL = /^(Kim|Advanced AI|AI)\b/;
const lowerFirst = (text: string): string => (!text || KEEPS_CAPITAL.test(text) ? text : text.charAt(0).toLowerCase() + text.slice(1));

const humanize = (value: string): string => value.replace(/_/g, ' ');

function valueText(condition: RuleCondition, catalog: RulesCatalog): string {
	const fact = catalog.facts.find((candidate) => candidate.id === condition.fact);
	const input = valueInputFor(fact, condition.op, catalog.operators);
	if (input === 'none') return '';
	const labelOf = (value: unknown) => {
		const option = fact?.options?.find((candidate) => candidate.value === value);
		if (option) return option.label;
		return input === 'intents' ? humanize(String(value)) : `"${String(value)}"`;
	};
	if (Array.isArray(condition.value)) {
		if (condition.value.length === 0) return '…';
		const labels = condition.value.map(labelOf);
		return labels.length > 3 ? `${labels.slice(0, 3).join(', ')} +${labels.length - 3}` : labels.join(', ');
	}
	if (condition.value === undefined || condition.value === '') return '…';
	return labelOf(condition.value);
}

/** "the message contains any of "refund"" */
export function describeCondition(condition: RuleCondition, catalog: RulesCatalog): string {
	const fact = catalog.facts.find((candidate) => candidate.id === condition.fact);
	const factLabel = lowerFirst(fact?.label ?? humanize(condition.fact));
	const value = valueText(condition, catalog);
	const operator = catalog.operators.find((candidate) => candidate.id === condition.op)?.label ?? humanize(condition.op);
	// A yes/no fact reads on its own ("it's the customer's first message"), with its question if it has one.
	if (condition.op === 'is_true' || condition.op === 'is_false') {
		const subject = value ? `${factLabel} ${value}` : factLabel;
		return condition.op === 'is_true' ? subject : `${subject} ${operator}`;
	}
	return [factLabel, operator, value].filter(Boolean).join(' ');
}

export function describeIf(step: IfStep, catalog: RulesCatalog): string {
	if (step.conditions.length === 0) return 'if …';
	const joiner = step.match === 'all' ? ' and ' : ' or ';
	return `if ${step.conditions.map((condition) => describeCondition(condition, catalog)).join(joiner)}`;
}

export function describeAction(step: ActionStep, catalog: RulesCatalog): string {
	const action = catalog.actions.find((candidate) => candidate.id === step.action);
	let phrase = action?.verb ?? humanize(step.action);
	if (step.action === 'reply' && step.params['mode'] === 'ai') phrase += ' in your brand voice';
	const listParam = action?.params.find((param) => param.input === 'tags');
	const list = listParam ? step.params[listParam.key] : undefined;
	if (Array.isArray(list) && list.length > 0) phrase += ` ${list.map((tag) => `"${String(tag)}"`).join(', ')}`;
	return phrase;
}

export function describeOutcome(outcome: OutcomeStep, catalog: RulesCatalog, trigger?: RuleTrigger): string {
	const offered = trigger ? outcomesFor(catalog, trigger) : catalog.outcomes;
	return lowerFirst(offered.find((candidate) => candidate.id === outcome.outcome)?.label ?? humanize(outcome.outcome));
}

/** One step's own words, without its branches. */
export function describeStep(step: RuleStep, catalog: RulesCatalog, trigger?: RuleTrigger): string {
	if (step.kind === 'if') return describeIf(step, catalog);
	if (step.kind === 'action') return describeAction(step, catalog);
	return describeOutcome(step, catalog, trigger);
}

function describeBranch(steps: RuleStep[], catalog: RulesCatalog, trigger: RuleTrigger): string {
	return steps.map((step) => describeTree(step, catalog, trigger)).join(', then ');
}

function describeTree(step: RuleStep, catalog: RulesCatalog, trigger: RuleTrigger): string {
	if (step.kind !== 'if') return describeStep(step, catalog, trigger);
	const then = step.then.length ? describeBranch(step.then, catalog, trigger) : '…';
	const otherwise = step.else.length ? `, otherwise ${describeBranch(step.else, catalog, trigger)}` : '';
	return `${describeIf(step, catalog)} → ${then}${otherwise}`;
}

/** The whole playbook as one line. */
export function summarizeRule(rule: Pick<RuleDefinition, 'trigger' | 'steps'>, catalog: RulesCatalog): string {
	const offered = catalog.triggers.find((candidate) => candidate.id === rule.trigger);
	const trigger = offered?.label ?? humanize(rule.trigger);
	const steps = rule.steps.length ? rule.steps.map((step) => describeTree(step, catalog, rule.trigger)).join(' · ') : 'nothing yet';
	// "After Kim replies" already says when; "When after Kim replies" doesn't read.
	return `${offered?.phase === 'after' ? trigger : `When ${lowerFirst(trigger)}`} · ${steps}`;
}
